import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Undo2, Crown, Clock, FileEdit } from "lucide-react";
import { type MatchWithPlayersAndClub, type Player } from "../../../shared/schema";
import { calculateEloChangeDynamic } from "@/lib/elo";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";


interface TVStyleScoreboardProps {
  match: MatchWithPlayersAndClub;
  onAddPoint: (team: "A" | "B") => void;
  onAddGame: (team: "A" | "B") => void;
  onViewMatch: () => void;
  onCancelMatch: () => void;
  onRevert: () => void;
  isLoading?: boolean;
  playerId?: string | null;
}

export function TVStyleScoreboard({
  match,
  onAddPoint,
  onAddGame,
  onViewMatch,
  onCancelMatch,
  onRevert,
  isLoading = false,
  playerId
}: TVStyleScoreboardProps) {
  const { t } = useTranslation();
  const [scoringMode, setScoringMode] = useState<"point" | "set">("set");

  // Fetch player data to check premium status
  const { data: player } = useQuery<Player>({
    queryKey: ["/api/players", playerId],
    queryFn: async () => {
      if (!playerId) return null;
      const response = await apiRequest("GET", `/api/players/${playerId}`);
      return response.json();
    },
    enabled: !!playerId,
  });

  // Fetch creator player data if creator is not in the match
  const creatorInMatch = match.createdByPlayerId && (
    match.teamAPlayer1?.id === match.createdByPlayerId ||
    match.teamAPlayer2?.id === match.createdByPlayerId ||
    match.teamBPlayer1?.id === match.createdByPlayerId ||
    match.teamBPlayer2?.id === match.createdByPlayerId
  );

  const { data: creatorPlayer } = useQuery<Player>({
    queryKey: ["/api/players", match.createdByPlayerId],
    queryFn: async () => {
      if (!match.createdByPlayerId) return null;
      const response = await apiRequest("GET", `/api/players/${match.createdByPlayerId}`);
      return response.json();
    },
    enabled: !!match.createdByPlayerId && !creatorInMatch,
  });

  // Extract last names only (same as history tab)
  const getFirstName = (fullName: string) => {
    const parts = fullName.split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
  };

  // Dynamic K-factor config (must match server/elo-config.ts)
  const dynamicConfig = {
    newPlayerKFactor: 40,
    topPlayerKFactor: 20,
    advancedPlayerKFactor: 30,
    lowerRatedKFactor: 35,
    minMatchesForExperience: 50,
    topPlayerThreshold: 2000,
    advancedPlayerThreshold: 1600,
  };

  // Calculate potential ELO changes for both teams using dynamic K-factor
  const calculatePotentialEloChanges = () => {
    // Match server logic exactly: average ELO, sum of matchesPlayed
    const teamAAvgElo = (match.teamAPlayer1.eloRating + match.teamAPlayer2.eloRating) / 2;
    const teamBAvgElo = (match.teamBPlayer1.eloRating + match.teamBPlayer2.eloRating) / 2;
    const teamAMatches = (match.teamAPlayer1.matchesPlayed ?? 0) + (match.teamAPlayer2.matchesPlayed ?? 0);
    const teamBMatches = (match.teamBPlayer1.matchesPlayed ?? 0) + (match.teamBPlayer2.matchesPlayed ?? 0);

    const eloChangeA_Wins = calculateEloChangeDynamic(teamAAvgElo, teamBAvgElo, true, teamAMatches, teamBMatches, dynamicConfig);
    const eloChangeA_Loses = calculateEloChangeDynamic(teamAAvgElo, teamBAvgElo, false, teamAMatches, teamBMatches, dynamicConfig);
    const eloChangeB_Wins = calculateEloChangeDynamic(teamBAvgElo, teamAAvgElo, true, teamBMatches, teamAMatches, dynamicConfig);
    const eloChangeB_Loses = calculateEloChangeDynamic(teamBAvgElo, teamAAvgElo, false, teamBMatches, teamAMatches, dynamicConfig);

    return {
      teamA: { ifWins: eloChangeA_Wins, ifLoses: eloChangeA_Loses },
      teamB: { ifWins: eloChangeB_Wins, ifLoses: eloChangeB_Loses }
    };
  };

  const eloPreview = calculatePotentialEloChanges();

  // Check if 3rd set is super tiebreak - Hoisted for use in render
  const isSuperTiebreak = (match.matchType === "best_of_3_supertiebreak" && match.currentSet === 3) ||
    (match.matchType === "best_of_3" && match.currentSet === 3 && match.teamAScore.length === 2);

  const getCurrentSetScore = (team: "A" | "B") => {

    if (isSuperTiebreak) {
      // For super tiebreak, show game scores directly (they represent points)
      return team === "A" ? match.teamAGameScore : match.teamBGameScore;
    }

    const currentSetIndex = match.currentSet - 1;
    const scores = team === "A" ? match.teamAScore : match.teamBScore;
    const numericScore = scores[currentSetIndex] || 0;

    return numericScore;
  };

  const getCurrentGameScore = (team: "A" | "B") => {
    // Get current game score (points within current game)
    const gameScore = team === "A" ? match.teamAGameScore : match.teamBGameScore;
    const opponentGameScore = team === "A" ? match.teamBGameScore : match.teamAGameScore;

    // Check if we're in a tiebreak situation
    const currentSetA = match.teamAScore.length > 0 ? match.teamAScore[match.teamAScore.length - 1] : 0;
    const currentSetB = match.teamBScore.length > 0 ? match.teamBScore[match.teamBScore.length - 1] : 0;

    let isTiebreak = false;
    let isSuperTiebreak = false;

    // Check if 3rd set is super tiebreak
    if ((match.matchType === "best_of_3_supertiebreak" && match.currentSet === 3) ||
      (match.matchType === "best_of_3" && match.currentSet === 3 && match.teamAScore.length === 2)) {
      isSuperTiebreak = true;
    } else if (match.matchType === "single_set_6" && currentSetA === 6 && currentSetB === 6) {
      isTiebreak = true;
    } else if (match.matchType === "single_set_9" && currentSetA === 8 && currentSetB === 8) {
      isTiebreak = true;
    } else if ((match.matchType === "best_of_3" || match.matchType === "best_of_3_full") && currentSetA === 6 && currentSetB === 6) {
      isTiebreak = true;
    }

    // In SET mode, not used. In PT mode, show tennis game scoring or tiebreak scoring
    if (isSuperTiebreak || isTiebreak) {
      return gameScore.toString(); // Show raw numbers for tiebreak/super tiebreak
    } else {
      return convertToTennisScore(gameScore, opponentGameScore);
    }
  };

  const convertToTennisScore = (playerScore: number, opponentScore: number): string => {
    // Standard tennis game scoring
    const scoreMap = ["0", "15", "30", "40"];

    // Both under 40 points (3)
    if (playerScore < 3 && opponentScore < 3) {
      return scoreMap[playerScore];
    }

    // At least one player has 40+ points
    if (playerScore >= 3 && opponentScore >= 3) {
      // Deuce or advantage situation
      if (playerScore === opponentScore) {
        return "40"; // Deuce (show as 40-40)
      } else if (playerScore > opponentScore) {
        return "AD"; // Advantage
      } else {
        return "40"; // Opponent has advantage, this player shows 40
      }
    }

    // One player has 40+, other has less
    if (playerScore >= 3) {
      return "40";
    } else {
      return scoreMap[playerScore];
    }
  };

  const getPreviousSetScore = (team: "A" | "B") => {
    if (match.currentSet <= 1) return null;
    const previousSetIndex = match.currentSet - 2;
    const scores = team === "A" ? match.teamAScore : match.teamBScore;
    return scores[previousSetIndex];
  };

  // Check if match is at the beginning (0-0)
  const isMatchStart = () => {
    // Check if we are in the first set or if no sets have been played
    if (match.currentSet > 1) return false;

    // Check if any points have been scored in the current set
    const currentSetA = match.teamAScore[0] || 0;
    const currentSetB = match.teamBScore[0] || 0;

    if (currentSetA > 0 || currentSetB > 0) return false;

    // Check if any game points have been scored
    if (match.teamAGameScore > 0 || match.teamBGameScore > 0) return false;

    return true;
  };

  return (
    <Card className="bg-white dark:bg-card border border-gray-200 dark:border-border overflow-hidden">
      {/* Clean Header */}
      <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-b dark:border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-xs font-medium text-gray-600 dark:text-muted-foreground">{t('matches.card.live')}</span>
            <button
              onClick={onCancelMatch}
              disabled={isLoading}
              className="ml-2 text-red-500 hover:text-red-700 transition-colors p-2"
              data-testid="button-cancel-match"
              title={t('matches.card.cancel')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-4">
            {/* Revert Button - Moved to left of Set Indicator */}
            <button
              onClick={onRevert}
              disabled={isLoading}
              className="order-first text-orange-600 hover:text-orange-700 hover:bg-orange-50 transition-colors p-2 rounded"
              data-testid="button-revert-score"
              title={t('matches.card.undo')}
            >
              <Undo2 className="w-4 h-4" />
            </button>

            <span className="order-last text-xs text-gray-500 dark:text-muted-foreground hidden sm:inline-block">
              {(() => {
                const currentSetA = match.teamAScore.length > 0 ? match.teamAScore[match.teamAScore.length - 1] : 0;
                const currentSetB = match.teamBScore.length > 0 ? match.teamBScore[match.teamBScore.length - 1] : 0;

                let isTiebreak = false;
                let isSuperTiebreak = false;

                // Check if 3rd set is super tiebreak
                if ((match.matchType === "best_of_3_supertiebreak" && match.currentSet === 3) ||
                  (match.matchType === "best_of_3" && match.currentSet === 3 && match.teamAScore.length === 2)) {
                  isSuperTiebreak = true;
                } else if (match.matchType === "single_set_6" && currentSetA === 6 && currentSetB === 6) {
                  isTiebreak = true;
                } else if (match.matchType === "single_set_9" && currentSetA === 8 && currentSetB === 8) {
                  isTiebreak = true;
                  isTiebreak = true;
                }

                return isSuperTiebreak ? t('matches.card.superTiebreak') : (isTiebreak ? t('matches.card.tiebreak') : t('matches.card.setNum', { num: match.currentSet }));
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* Minimal Scoreboard */}
      <div className="p-4">
        {/* Team A */}
        <button
          onClick={() => (scoringMode === "point" || isSuperTiebreak) ? onAddPoint("A") : onAddGame("A")}
          disabled={isLoading}
          className="w-full flex items-center justify-between py-3 border-b border-gray-100 dark:border-border hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
          data-testid="button-add-point-team-a"
        >
          <div className="flex flex-col items-start px-3 py-2 transition-all">
            <div className="font-medium text-gray-900 dark:text-foreground group-hover:text-blue-600">
              {getFirstName(match.teamAPlayer1.name)} / {getFirstName(match.teamAPlayer2.name)}
            </div>
          </div>

          {/* Tap to score hint - Visible only at start, responsive text size */}
          {isMatchStart() && (
            <div className="flex-1 flex justify-center opacity-20 mx-1">
              <span className="text-[10px] sm:text-sm md:text-lg font-bold uppercase tracking-widest text-gray-500 dark:text-white whitespace-nowrap animate-pulse">
                {t('matches.card.tapToScore')}
              </span>
            </div>
          )}

          {/* ELO Preview for Team A - Premium only, hidden at match start */}
          {player?.isPremium && !isMatchStart() && (
            <div className="text-xs text-gray-500 mx-4 flex items-center space-x-1">
              <span className="text-blue-600">+{eloPreview.teamA.ifWins}</span> /
              <span className="text-red-600"> {eloPreview.teamA.ifLoses}</span>
              <Crown className="w-3 h-3 text-yellow-500" />
            </div>
          )}

          <div className="flex items-center gap-4 pr-2">
            {/* All previous set scores */}
            {Array.from({ length: match.currentSet - 1 }).map((_, index) => (
              <div key={index} className="text-sm text-gray-400 min-w-[24px] text-center">
                {match.teamAScore[index] || 0}
              </div>
            ))}
            {/* Current Set Score (always numeric) */}
            <div className="text-lg font-bold text-gray-900 dark:text-foreground min-w-[32px] text-center">
              {getCurrentSetScore("A")}
            </div>
            {/* Current Game Score (only in PT mode and NOT in Super Tiebreak) */}
            {scoringMode === "point" && !isSuperTiebreak && (
              <div className="text-2xl font-bold text-blue-600 min-w-[40px] text-center">
                {getCurrentGameScore("A")}
              </div>
            )}


          </div>
        </button>

        {/* Team B */}
        <button
          onClick={() => (scoringMode === "point" || isSuperTiebreak) ? onAddPoint("B") : onAddGame("B")}
          disabled={isLoading}
          className="w-full flex items-center justify-between py-3 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
          data-testid="button-add-point-team-b"
        >
          <div className="flex flex-col items-start px-3 py-2 transition-all">
            <div className="font-medium text-gray-900 dark:text-foreground group-hover:text-blue-600">
              {getFirstName(match.teamBPlayer1.name)} / {getFirstName(match.teamBPlayer2.name)}
            </div>
          </div>

          {/* Tap to score hint - Visible only at start, responsive text size */}
          {isMatchStart() && (
            <div className="flex-1 flex justify-center opacity-20 mx-1">
              <span className="text-[10px] sm:text-sm md:text-lg font-bold uppercase tracking-widest text-gray-500 dark:text-white whitespace-nowrap animate-pulse">
                {t('matches.card.tapToScore')}
              </span>
            </div>
          )}

          {/* ELO Preview for Team B - Premium only, hidden at match start */}
          {player?.isPremium && !isMatchStart() && (
            <div className="text-xs text-gray-500 mx-4 flex items-center space-x-1">
              <span className="text-blue-600">+{eloPreview.teamB.ifWins}</span> /
              <span className="text-red-600"> {eloPreview.teamB.ifLoses}</span>
              <Crown className="w-3 h-3 text-yellow-500" />
            </div>
          )}

          <div className="flex items-center gap-4 pr-2">
            {/* All previous set scores */}
            {Array.from({ length: match.currentSet - 1 }).map((_, index) => (
              <div key={index} className="text-sm text-gray-400 min-w-[24px] text-center">
                {match.teamBScore[index] || 0}
              </div>
            ))}
            {/* Current Set Score (always numeric) */}
            <div className="text-lg font-bold text-gray-900 dark:text-foreground min-w-[32px] text-center">
              {getCurrentSetScore("B")}
            </div>
            {/* Current Game Score (only in PT mode and NOT in Super Tiebreak) */}
            {scoringMode === "point" && !isSuperTiebreak && (
              <div className="text-2xl font-bold text-blue-600 min-w-[40px] text-center">
                {getCurrentGameScore("B")}
              </div>
            )}


          </div>
        </button>
      </div>

      {/* Duration and Club Info */}
      <div className="bg-gray-50 dark:bg-muted px-4 py-2 border-t dark:border-border">
        <div className="flex justify-between items-center text-xs text-gray-600 dark:text-muted-foreground">
          <div className="flex items-center gap-3">
            {/* Duration */}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-gray-500" />
              {getDuration()}
            </span>
            {/* Creator */}
            {match.createdByPlayerId && (() => {
              // First try to find creator in match players
              const creatorInMatch =
                match.teamAPlayer1?.id === match.createdByPlayerId ? match.teamAPlayer1 :
                  match.teamAPlayer2?.id === match.createdByPlayerId ? match.teamAPlayer2 :
                    match.teamBPlayer1?.id === match.createdByPlayerId ? match.teamBPlayer1 :
                      match.teamBPlayer2?.id === match.createdByPlayerId ? match.teamBPlayer2 :
                        null;

              // If not in match, use fetched creator player
              const creator = creatorInMatch || creatorPlayer;
              const creatorName = creator ? getFirstName(creator.name) : null;

              return creatorName ? (
                <span className="flex items-center gap-1">
                  <FileEdit className="w-3 h-3 text-gray-500" />
                  {creatorName}
                </span>
              ) : null;
            })()}
          </div>
          {/* Club */}
          {match.club && (
            <span className="flex items-center gap-1">
              <img src="/tennis-ball.png" alt="Club" className="w-3 h-3" />
              {match.club.name}
            </span>
          )}
        </div>
      </div>

    </Card>
  );

  function getDuration() {
    const start = new Date(match.startTime);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - start.getTime()) / 1000);

    const minutes = Math.floor(diffInSeconds / 60);
    const seconds = diffInSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}